import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Form,
  Input,
  Button,
  Select,
  InputNumber,
  Card,
  Upload,
  message,
  Spin,
  Space,
  Image,
  Table,
  Tag,
} from 'antd';
import {
  SaveOutlined,
  CloseOutlined,
  UploadOutlined,
  SyncOutlined,
  ArrowRightOutlined,
  DollarOutlined,
  CalendarOutlined,
  UserOutlined,
  FileTextOutlined,
} from '@ant-design/icons';
import MainLayout from '../../layouts/MainLayout';
import { recurringExpensesApi } from '../../utils/api';
import PersianDatePicker from '../../components/PersianDatePicker';
import { formatPriceToman } from '../../utils/formatPriceToman';
import { handleFormSubmitError } from '../../utils/errorHandler';
import { formatJalaliApiDate } from '../../utils/dateUtils';
import { useDetailReturnRefs } from '../../hooks/useListReturnState';

const { Option } = Select;
const { TextArea } = Input;

const toPersianNumber = (num) => {
  if (num === null || num === undefined) return '-';
  const persianDigits = ['۰', '۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹'];
  return String(num).replace(/\d/g, (d) => persianDigits[parseInt(d)]);
};

const rotationOptions = [
  { value: 'daily', label: 'روزانه' },
  { value: 'weekly', label: 'هفتگی' },
  { value: 'bi-weekly', label: 'دو هفته‌ای' },
  { value: 'monthly', label: 'ماهانه' },
  { value: 'quarterly', label: 'سه ماهه' },
  { value: 'half-yearly', label: 'شش ماهه' },
  { value: 'annually', label: 'سالانه' },
];

const EditRecurringExpense = () => {
  const navigate = useNavigate();
  const { returnToRef, buildGoBackState } = useDetailReturnRefs();
  const { id } = useParams();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [currencies, setCurrencies] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [projects, setProjects] = useState([]);
  const [categories, setCategories] = useState([]);
  const [banks, setBanks] = useState([]);
  const [billFile, setBillFile] = useState(null);
  const [existingBillUrl, setExistingBillUrl] = useState(null);
  const [recurrings, setRecurrings] = useState([]);
  const [expenseCount, setExpenseCount] = useState(0);

  const goBackToList = () => {
    navigate(returnToRef.current || '/finance/recurring-expenses', { state: buildGoBackState() });
  };

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await recurringExpensesApi.getEditData(id);
      if (res?.data) {
        setCurrencies(res.data.currencies || []);
        setEmployees(res.data.employees || []);
        setProjects(res.data.projects || []);
        setCategories(res.data.categories || []);
        setBanks(res.data.banks || []);
        setExpenseCount(res.data.expense_count || 0);

        if (res.data.expense) {
          const expense = res.data.expense;
          setRecurrings(expense.recurrings || []);

          // تبدیل تاریخ به شمسی
          let issueDate = '';
          if (expense.issue_date) {
            issueDate = formatJalaliApiDate(expense.issue_date);
          }

          form.setFieldsValue({
            item_name: expense.item_name,
            price: expense.price,
            currency_id: expense.currency_id,
            category_id: expense.category_id,
            user_id: expense.user_id,
            status: expense.status,
            rotation: expense.rotation,
            billing_cycle: expense.billing_cycle || 1,
            description: expense.description,
            purchase_from: expense.purchase_from,
            bank_account_id: expense.bank_account_id,
            project_id: expense.project_id,
            issue_date: issueDate,
          });

          if (expense.bill_url) {
            setExistingBillUrl(expense.bill_url);
          }
        }
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      message.error('خطا در دریافت اطلاعات هزینه دوره‌ای');
    } finally {
      setLoading(false);
    }
  };

  const handleBillUpload = (info) => {
    if (info.file) {
      setBillFile(info.file);
    }
    return false;
  };

  const handleSubmit = async (values) => {
    setSubmitting(true);
    try {
      let issueDate = null;
      if (values.issue_date) {
        issueDate = formatJalaliApiDate(values.issue_date);
      }

      const payload = {
        expense_count: expenseCount,
        item_name: values.item_name,
        price: values.price,
        currency_id: values.currency_id,
        category_id: values.category_id || undefined,
        user_id: values.user_id || undefined,
        rotation: values.rotation,
        billing_cycle: values.billing_cycle || undefined,
        description: values.description || undefined,
        purchase_from: values.purchase_from || undefined,
        bank_account_id: values.bank_account_id || undefined,
        issue_date: issueDate,
        project_id: values.project_id || undefined,
        status: values.status,
      };

      if (billFile) {
        payload.bill = billFile;
      }

      await recurringExpensesApi.update(id, payload);
      message.success('هزینه دوره‌ای با موفقیت ویرایش شد');
      navigate(returnToRef.current || '/finance/recurring-expenses', { state: buildGoBackState() });
    } catch (error) {
      console.error('Error updating recurring expense:', error);
      handleFormSubmitError(form, error, 'خطا در ویرایش هزینه دوره‌ای');
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return toPersianNumber(date.toLocaleDateString('fa-IR'));
  };

  const recurringColumns = [
    {
      title: <span style={{ fontFamily: 'CustomFont' }}>نام هزینه</span>,
      dataIndex: 'item_name',
      key: 'item_name',
      render: (text) => <span style={{ fontFamily: 'CustomFont' }}>{text}</span>,
    },
    {
      title: <span style={{ fontFamily: 'CustomFont' }}>مبلغ</span>,
      dataIndex: 'price',
      key: 'price',
      render: (price) => (
        <span className="text-blue-600 font-medium" style={{ fontFamily: 'CustomFont' }}>
          {formatPriceToman(price)}
        </span>
      ),
    },
    {
      title: <span style={{ fontFamily: 'CustomFont' }}>تاریخ خرید</span>,
      dataIndex: 'purchase_date',
      key: 'purchase_date',
      render: (date) => <span style={{ fontFamily: 'CustomFont' }}>{formatDate(date)}</span>,
    },
    {
      title: <span style={{ fontFamily: 'CustomFont' }}>وضعیت</span>,
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        const map = {
          pending: { color: 'warning', text: 'در انتظار' },
          approved: { color: 'success', text: 'تأیید شده' },
          rejected: { color: 'error', text: 'رد شده' },
        };
        const s = map[status] || { color: 'default', text: status };
        return <Tag color={s.color} style={{ fontFamily: 'CustomFont' }}>{s.text}</Tag>;
      },
    },
  ];

  if (loading) {
    return (
      <MainLayout>
        <div className="flex justify-center items-center h-64">
          <Spin size="large" />
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="" style={{ fontFamily: 'CustomFont' }}>
        <Card className="shadow-xl border-0" styles={{ body: { padding: 0 } }}>
          {/* Header */}
          <div className="flex items-center justify-between flex-wrap gap-4 p-6 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 rounded-t-xl">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-purple-50 dark:bg-purple-900/20 flex items-center justify-center">
                <SyncOutlined className="text-purple-600 dark:text-purple-400 text-xl" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100 m-0" style={{ fontFamily: 'CustomFont' }}>
                  ویرایش هزینه دوره‌ای
                </h1>
                <p className="text-gray-500 dark:text-gray-400 text-sm m-0">
                  ویرایش اطلاعات هزینه دوره‌ای
                </p>
              </div>
            </div>
            <Space>
              <Button
                icon={<ArrowRightOutlined />}
                onClick={goBackToList}
                style={{ fontFamily: 'CustomFont' }}
              >
                بازگشت
              </Button>
            </Space>
          </div>

          {/* Content */}
          <div className="p-6 bg-gray-50 dark:bg-gray-900/50 rounded-b-xl">
            <Form form={form} layout="vertical" onFinish={handleSubmit} scrollToFirstError={{ behavior: 'smooth', block: 'center' }}>
              {/* بخش 1: اطلاعات اصلی هزینه */}
              <Card
                className="dark:bg-gray-800 border-0 shadow-sm mb-4"
                title={
                  <div className="flex items-center gap-2">
                    <DollarOutlined className="text-green-600" />
                    <span style={{ fontFamily: 'CustomFont' }}>اطلاعات اصلی هزینه</span>
                  </div>
                }
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Form.Item
                    name="item_name"
                    label={<span style={{ fontFamily: 'CustomFont' }}>نام هزینه</span>}
                    rules={[{ required: true, message: 'لطفاً نام هزینه را وارد کنید' }]}
                  >
                    <Input placeholder="نام هزینه" style={{ fontFamily: 'CustomFont' }} />
                  </Form.Item>

                  <Form.Item
                    name="price"
                    label={<span style={{ fontFamily: 'CustomFont' }}>مبلغ</span>}
                    rules={[{ required: true, message: 'لطفاً مبلغ را وارد کنید' }]}
                  >
                    <InputNumber
                      className="w-full"
                      placeholder="مبلغ هزینه"
                      formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                      parser={(value) => value.replace(/,/g, '')}
                      style={{ fontFamily: 'CustomFont' }}
                    />
                  </Form.Item>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Form.Item
                    name="currency_id"
                    label={<span style={{ fontFamily: 'CustomFont' }}>واحد پول</span>}
                    rules={[{ required: true, message: 'لطفاً واحد پول را انتخاب کنید' }]}
                  >
                    <Select placeholder="انتخاب واحد پول" style={{ fontFamily: 'CustomFont' }}>
                      {currencies.map((cur) => (
                        <Option key={cur.id} value={cur.id}>
                          {cur.currency_symbol} ({cur.currency_code})
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>

                  <Form.Item
                    name="purchase_from"
                    label={<span style={{ fontFamily: 'CustomFont' }}>خریداری از</span>}
                  >
                    <Input placeholder="نام فروشنده یا مغازه" style={{ fontFamily: 'CustomFont' }} />
                  </Form.Item>
                </div>
              </Card>

              {/* بخش 2: تنظیمات دوره‌ای */}
              <Card
                className="dark:bg-gray-800 border-0 shadow-sm mb-4"
                title={
                  <div className="flex items-center gap-2">
                    <SyncOutlined className="text-purple-600" />
                    <span style={{ fontFamily: 'CustomFont' }}>تنظیمات دوره‌ای</span>
                  </div>
                }
              >
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Form.Item
                    name="rotation"
                    label={<span style={{ fontFamily: 'CustomFont' }}>تناوب تکرار</span>}
                    rules={[{ required: true, message: 'لطفاً تناوب تکرار را انتخاب کنید' }]}
                  >
                    <Select placeholder="انتخاب تناوب" style={{ fontFamily: 'CustomFont' }}>
                      {rotationOptions.map((opt) => (
                        <Option key={opt.value} value={opt.value}>
                          {opt.label}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>

                  <Form.Item
                    name="billing_cycle"
                    label={<span style={{ fontFamily: 'CustomFont' }}>دوره صورتحساب</span>}
                  >
                    <InputNumber
                      className="w-full"
                      min={1}
                      placeholder="تعداد دوره"
                      style={{ fontFamily: 'CustomFont' }}
                    />
                  </Form.Item>

                  <Form.Item
                    name="status"
                    label={<span style={{ fontFamily: 'CustomFont' }}>وضعیت</span>}
                  >
                    <Select placeholder="انتخاب وضعیت" style={{ fontFamily: 'CustomFont' }}>
                      <Option value="active">فعال</Option>
                      <Option value="inactive">غیرفعال</Option>
                    </Select>
                  </Form.Item>
                </div>
              </Card>

              {/* بخش 3: تاریخ شروع */}
              <Card
                className="dark:bg-gray-800 border-0 shadow-sm mb-4"
                title={
                  <div className="flex items-center gap-2">
                    <CalendarOutlined className="text-blue-600" />
                    <span style={{ fontFamily: 'CustomFont' }}>تاریخ شروع</span>
                  </div>
                }
              >
                <Form.Item
                  name="issue_date"
                  label={<span style={{ fontFamily: 'CustomFont' }}>تاریخ شروع</span>}
                >
                  <PersianDatePicker
                    placeholder="انتخاب تاریخ"
                    style={{ fontFamily: 'CustomFont' }}
                  />
                </Form.Item>
              </Card>

              {/* بخش 4: ارجاعات */}
              <Card
                className="dark:bg-gray-800 border-0 shadow-sm mb-4"
                title={
                  <div className="flex items-center gap-2">
                    <UserOutlined className="text-orange-600" />
                    <span style={{ fontFamily: 'CustomFont' }}>ارجاعات</span>
                  </div>
                }
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Form.Item
                    name="user_id"
                    label={<span style={{ fontFamily: 'CustomFont' }}>کارمند</span>}
                  >
                    <Select
                      placeholder="انتخاب کارمند"
                      style={{ fontFamily: 'CustomFont' }}
                      showSearch
                      optionFilterProp="children"
                      allowClear
                    >
                      {employees.map((emp) => (
                        <Option key={emp.id} value={emp.id}>
                          {emp.name}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>

                  <Form.Item
                    name="project_id"
                    label={<span style={{ fontFamily: 'CustomFont' }}>پروژه</span>}
                  >
                    <Select
                      placeholder="انتخاب پروژه"
                      style={{ fontFamily: 'CustomFont' }}
                      showSearch
                      optionFilterProp="children"
                      allowClear
                    >
                      {projects.map((proj) => (
                        <Option key={proj.id} value={proj.id}>
                          {proj.project_name}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Form.Item
                    name="category_id"
                    label={<span style={{ fontFamily: 'CustomFont' }}>دسته‌بندی هزینه</span>}
                  >
                    <Select placeholder="انتخاب دسته‌بندی" style={{ fontFamily: 'CustomFont' }} allowClear>
                      {categories.map((cat) => (
                        <Option key={cat.id} value={cat.id}>
                          {cat.category_name}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>

                  <Form.Item
                    name="bank_account_id"
                    label={<span style={{ fontFamily: 'CustomFont' }}>حساب بانکی</span>}
                  >
                    <Select placeholder="انتخاب حساب بانکی" style={{ fontFamily: 'CustomFont' }} allowClear>
                      {banks.map((bank) => (
                        <Option key={bank.id} value={bank.id}>
                          {bank.bank_name || 'صندوق نقدی'}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </div>
              </Card>

              {/* بخش 5: توضیحات و فایل */}
              <Card
                className="dark:bg-gray-800 border-0 shadow-sm mb-4"
                title={
                  <div className="flex items-center gap-2">
                    <FileTextOutlined className="text-gray-600" />
                    <span style={{ fontFamily: 'CustomFont' }}>توضیحات و فایل</span>
                  </div>
                }
              >
                <Form.Item
                  name="description"
                  label={<span style={{ fontFamily: 'CustomFont' }}>توضیحات</span>}
                >
                  <TextArea rows={3} placeholder="توضیحات اختیاری..." style={{ fontFamily: 'CustomFont' }} />
                </Form.Item>

                {/* نمایش فایل فعلی */}
                {existingBillUrl && (
                  <div className="mb-4">
                    <span className="block mb-2 font-medium" style={{ fontFamily: 'CustomFont' }}>
                      فاکتور فعلی:
                    </span>
                    {existingBillUrl.match(/\.(jpg|jpeg|png|gif|webp)$/i) ? (
                      <Image src={existingBillUrl} width={150} className="rounded-lg" />
                    ) : (
                      <a href={existingBillUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600">
                        مشاهده فایل
                      </a>
                    )}
                  </div>
                )}

                <Form.Item
                  name="bill"
                  label={<span style={{ fontFamily: 'CustomFont' }}>فاکتور / رسید جدید</span>}
                >
                  <Upload
                    beforeUpload={() => false}
                    onChange={handleBillUpload}
                    maxCount={1}
                    accept="image/*,.pdf"
                    listType="picture"
                  >
                    <Button icon={<UploadOutlined />} style={{ fontFamily: 'CustomFont' }}>
                      انتخاب فایل جدید
                    </Button>
                  </Upload>
                </Form.Item>
              </Card>

              {/* دکمه‌های عملیات */}
              <div className="flex justify-end gap-4 mt-6">
                <Button
                  icon={<CloseOutlined />}
                  onClick={goBackToList}
                  style={{ fontFamily: 'CustomFont' }}
                >
                  انصراف
                </Button>
                <Button
                  type="primary"
                  icon={<SaveOutlined />}
                  htmlType="submit"
                  loading={submitting}
                  className="bg-green-600 hover:bg-green-700 border-0"
                  style={{ fontFamily: 'CustomFont' }}
                >
                  ذخیره تغییرات
                </Button>
              </div>
            </Form>

            {/* هزینه‌های ثبت شده از این تکرار */}
            {recurrings.length > 0 && (
              <Card
                className="dark:bg-gray-800 border-0 shadow-sm mt-6"
                title={
                  <span style={{ fontFamily: 'CustomFont' }}>
                    هزینه‌های ثبت شده ({toPersianNumber(recurrings.length)})
                  </span>
                }
              >
                <Table
                  columns={recurringColumns}
                  dataSource={recurrings}
                  rowKey="id"
                  pagination={false}
                  size="small"
                />
              </Card>
            )}
          </div>
        </Card>
      </div>
    </MainLayout>
  );
};

export default EditRecurringExpense;
